import { isUserAuthenticated } from "@/middleware/auth";
import {
  addChatToLocalDB,
  getChatFromLocalDB,
  removeChatFromLocalDB,
  updateChatInLocalDB,
} from "@/indexedDB/services/chats";
import { parseP2PPeer } from "@/lib/p2pPeer";
import { isDeletedUserLabel } from "@/lib/deletedSender";

import { ChatEntity, InitChatStatus } from "@/indexedDB/types";

import { SocketEventActionType, P2PChatEventType } from "./types";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";
import { useNavigationState } from "@/zustand/NavigationState";
import { removeChatDataFromLocalDB } from "@/indexedDB/services/chatTeardown";
import { navigateToChatList } from "@/lib/navigationRef";
import { extractTimestampFromUUID7 } from "@/utils/uuid7";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";

export const handleP2PChatEvent = async (data: P2PChatEventType) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  switch (data.action) {
    case SocketEventActionType.CREATE:
      await onCreateP2PChat({ eventPayload: data });
      break;
    case SocketEventActionType.UPDATE:
      await onUpdateP2PChat({ eventPayload: data });
      break;
    case SocketEventActionType.DELETE:
      await onDeleteP2PChat({ eventPayload: data });
      break;
    default:
      break;
  }
};

const onCreateP2PChat = async ({
  eventPayload,
}: {
  eventPayload: P2PChatEventType;
}) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  // user_two is a JSON-object string on legacy events and a PLAIN username
  // string on Umbra events (incl. the "Deleted User" label) — never throw.
  const peer = parseP2PPeer(eventPayload.p2p_chat.user_two);
  if (!peer) {
    return;
  }

  const newP2PChat: ChatEntity = {
    id: eventPayload.p2p_chat.id,
    hubId: null,
    eCSK: eventPayload.p2p_chat.secure_payload,
    currentEpoch: 0,
    name: peer.username,
    type: ChatType.P2P,
    avatar: {
      attachmentId: peer.profileImage ?? "",
      secondMemberId: peer.username,
    },
    timestamp: eventPayload.p2p_chat.timestamp,
    // lastSyncedAt: new Date().toISOString(),
    lastSyncedAt: extractTimestampFromUUID7(eventPayload.p2p_chat.id),
    memberCount: 2,
    unreadCount: 0,
    isMuted: false,
    isPinned: false,
    isArchived: false,
    isPublic: false,
    allowThreads: false,
    canPost: MemberRole.MEMBER,

    initStatus: InitChatStatus.SUCCESS,
    roleAsMember: {
      username: userFingerPrint.username,
      role:
        userFingerPrint.username === peer.username
          ? MemberRole.MEMBER
          : MemberRole.OWNER,
    },
    lastMessage: null,
    firstPhaseMsgListInit: true,
    fullMsgListInit: true,
  };
  await addChatToLocalDB(newP2PChat);

  // TO DO => global notifications layer - this is just for testing purpose
  // const audio = new Audio(
  //   `${process.env.PUBLIC_URL}/sounds/notification-2.mp3`,
  // );
  // /* eslint-disable no-console */
  // audio.play().catch((error) => console.log("Audio playback failed:", error));
};

// TO DO => needs improvement on syncing usrer avatar across all components
const onUpdateP2PChat = async ({
  eventPayload,
}: {
  eventPayload: P2PChatEventType;
}) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  const activeChatId = useNavigationState.getState().activeChatId;
  const setActiveChatAvatar = useNavigationState.getState().setActiveChatAvatar;
  const setActiveChatName = useNavigationState.getState().setActiveChatName;

  // user_two is a JSON-object string on legacy events and a PLAIN username
  // string on Umbra events. The account-deletion rename arrives here as
  // user_two === "Deleted User" (tailored per recipient: user_one is always
  // this client's user) — the old JSON.parse threw on it and the event was
  // silently dropped, leaving the chat list on the real username forever.
  const peer = parseP2PPeer(eventPayload.p2p_chat.user_two);
  if (!peer) {
    return;
  }
  const isCurrentUser = userFingerPrint.username === peer.username;
  const chatId = eventPayload.p2p_chat.id;

  // If current user is not the second member of the chat.
  if (!isCurrentUser) {
    const peerIsDeleted = isDeletedUserLabel(peer.username);

    // Account deletion: this Update is the ONLY signal a DM-only peer's
    // client receives (the UserDeleted notice is posted to group chats only).
    // The payload doesn't carry the old name — read it from the existing row
    // and purge its persisted avatar-map entry before overwriting.
    if (peerIsDeleted) {
      const existingChat = await getChatFromLocalDB(chatId);
      const oldUsername =
        existingChat?.avatar?.secondMemberId || existingChat?.name;
      if (oldUsername && !isDeletedUserLabel(oldUsername)) {
        useUserAvatarMapState.getState().removeUserAvatar(oldUsername, true);
      }
    }

    // Explicit write list — NEVER spread the payload: the deletion Update
    // carries secure_payload: "" (would wipe the stored eCSK) and
    // timestamp: 0 (would corrupt chat ordering).
    await updateChatInLocalDB(chatId, {
      name: peer.username,
      avatar: {
        attachmentId: peer.profileImage ?? "",
        secondMemberId: peer.username,
      },
    });

    // update active chat name/avatar if navigated on same chat id
    if (activeChatId === chatId) {
      setActiveChatAvatar({
        attachmentId: peer.profileImage ?? "",
        secondMemberId: peer.username,
      });
      setActiveChatName(peer.username);
    }

    // Keep UserAvatarMapState in sync — ChatAvatar delegates P2P
    // rendering to UserAvatar which reads from this map. Never write an
    // entry for the deleted-user label itself (UserAvatar renders the label
    // with a neutral glyph and never consults the map for it).
    if (peerIsDeleted) {
      return;
    }
    if (peer.profileImage) {
      useUserAvatarMapState
        .getState()
        .setUserAvatar(
          peer.username,
          peer.profileImage,
          new Date().toISOString(),
          true,
        );
    } else {
      useUserAvatarMapState.getState().removeUserAvatar(peer.username, true);
    }
  }
};

const onDeleteP2PChat = async ({
  eventPayload,
}: {
  eventPayload: P2PChatEventType;
}) => {
  // if chat id is active
  const activeChatId = useNavigationState.getState().activeChatId;
  // Navigate away via URL if the deleted chat is active (URL-first pattern)
  if (eventPayload.p2p_chat.id === activeChatId) {
    navigateToChatList();
  }

  // delete messages + attachments (incl. OPFS blobs) + links from local db
  await removeChatDataFromLocalDB(eventPayload.p2p_chat.id);
  // remove chat from local db
  await removeChatFromLocalDB(eventPayload.p2p_chat.id);
};
